// @flow
import * as React from 'react';
import styled from 'styled-components';
import { updateArrayItem, removeArrayIndex, uuid } from 'utils/functional';
import { InputField } from 'components/Form';
import Box from 'components/Box';
import Link from 'components/Link';
import { BorderlessButton } from 'components/Button';
import Container from 'components/Container';
import { colors } from '../theme';

const { useState } = React;

const AddRemoveButton = styled(BorderlessButton)`
  font-size: 32px;
  transition: color 0.2s ease-in;
  &:hover {
    color: ${colors.turtleGreen};
  }
  &:disabled {
    color: ${colors.coolGrey};
  }
`;

const MultiInput = ({
  id,
  component,
  name,
  values,
  maxInputs,
  onChange,
  type = 'text',
  placeholder,
  hasError,
  disabled,
  extraData = [],
  hideControls = false,
  addText,
  removeText,
  showEmpty,
  startWithNone,
  isComplex,
  sortBy,
  ...props
}: {
  id?: string,
  component?: ?React.ElementType,
  name: string,
  values: Array<string | Object>,
  maxInputs: number,
  onChange: (Array<string>) => void,
  type?: string,
  placeholder?: string,
  hasError?: boolean,
  disabled?: boolean,
  extraData?: Array<Object>,
  hideControls?: boolean,
  addText?: string,
  removeText?: string,
  showEmpty?: boolean,
  startWithNone?: boolean,
  isComplex?: boolean,
  'data-qa-id': string,
  sortBy?: (
    { value: mixed, index: number },
    { value: mixed, index: number }
  ) => number
}) => {
  values =
    values.length !== 0 ? values : startWithNone ? [] : isComplex ? [{}] : [''];
  const Component = component || InputField;

  const [ids, setIds] = useState(
    new Array(values.length).fill('').map(() => uuid())
  );

  const onInputChange = index => ev => {
    let newValues;
    if (showEmpty && values.length > 1 && !ev) {
      newValues = removeArrayIndex(values, index);
      setIds(removeArrayIndex(ids, index));
    } else {
      newValues = updateArrayItem(
        values,
        index,
        ev && ev.target ? ev.target.value : ev
      );
      if (showEmpty && values.length < maxInputs) {
        newValues.push(isComplex ? {} : '');
      }
      if (newValues.length > values.length) {
        setIds([...ids, uuid()]);
      }
    }
    onChange(newValues);
  };
  const addInputClicked = () => {
    const newValues = updateArrayItem(
      values,
      values.length,
      isComplex ? {} : ''
    );
    onChange(newValues);
    setIds([...ids, uuid()]);
  };
  const removeInputClicked = () => {
    onChange(removeArrayIndex(values, values.length - 1));
    setIds(removeArrayIndex(ids, values.length - 1));
  };
  const inputId = index => {
    if (!id) return undefined;
    return index === 0 ? id : `${id}-${index + 1}`;
  };

  const inputs = values
    .map((value, index) => ({
      value,
      index
    }))
    .sort(sortBy);

  return (
    <>
      {inputs.map(({ value, index }) => (
        <Container
          mb="8px"
          key={ids[index] || index} // eslint-disable-line react/no-array-index-key
        >
          {removeText &&
            (values.length > 1 || (startWithNone && values.length > 0)) && (
              <Box mt={36} mb="8px">
                <Link
                  href="#"
                  data-qa-id={`${props['data-qa-id']}-remove`}
                  onClick={e => {
                    e.preventDefault();
                    if (!disabled) {
                      removeInputClicked();
                    }
                  }}
                >
                  - {removeText}
                </Link>
              </Box>
            )}
          <Component
            id={inputId(index)}
            name={`${name}.${index}`}
            data-qa-id={`${props['data-qa-id']}`}
            type={type}
            placeholder={placeholder}
            value={value}
            onChange={onInputChange(index)}
            disabled={disabled}
            hasError={hasError}
            {...extraData[index]}
          />
        </Container>
      ))}
      {addText && values.length < maxInputs && (
        <Box mt={36}>
          <Link
            href="#"
            data-qa-id={`${props['data-qa-id']}-add`}
            onClick={e => {
              e.preventDefault();
              if (!disabled) {
                addInputClicked();
              }
            }}
          >
            + {addText}
          </Link>
        </Box>
      )}
      {!hideControls && (
        <>
          <AddRemoveButton
            mr="10px"
            p="0"
            disabled={disabled || values.length === maxInputs}
            data-qa-id={`${props['data-qa-id']}-add`}
            onClick={addInputClicked}
          >
            + {/* TODO: use icon */}
          </AddRemoveButton>
          <AddRemoveButton
            p="0"
            disabled={disabled || values.length === 1}
            data-qa-id={`${props['data-qa-id']}-remove`}
            onClick={removeInputClicked}
          >
            - {/* TODO: use icon */}
          </AddRemoveButton>
        </>
      )}
    </>
  );
};

export default MultiInput;
